// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { getBankUploadExcelFileSizeError } from '@client/banks/excel/bank-upload-excel-limits';
import type { BankUploadTemplateEntity } from '@client/banks/utils/download-bank-upload-template';
import { isDefined, isNull } from '@shared/utils';
import { useSnackbar } from '@shared/ui-kit/providers/snackbar-provider';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatBankUploadParseError } from './format-bank-upload-parse-error';
import { parseBankUploadWorkbook } from './parse-bank-upload-workbook';
import type {
  BankUploadParseError,
  BankUploadParseResult,
  ParseBankUploadWorkbookOptions,
} from './types';

const TEMPLATE_ERROR_CODES = new Set([
  'missing_meta',
  'invalid_template',
  'entity_mismatch',
  'unsupported_schema',
  'missing_sheet',
  'invalid_headers',
  'invalid_file',
]);

const LIMIT_ERROR_CODES = new Set(['file_too_large', 'too_many_rows']);

/**
 * Parse + validate an uploaded template. Keeps row/field errors for inline UI,
 * snackbar summary, and console detail.
 */
export const useParseBankUploadFile = (entity: BankUploadTemplateEntity) => {
  const { enqueueSnackbar } = useSnackbar();

  const { t } = useTranslation();

  const [isParsing, setIsParsing] = useState(false);

  const [parseErrors, setParseErrors] = useState<BankUploadParseError[]>([]);

  const clearParseErrors = useCallback(() => {
    setParseErrors([]);
  }, []);

  const applyFileSizeLimit = useCallback(
    (file: File | null): boolean => {
      if (isNull(file)) {
        setParseErrors([]);

        return true;
      }

      const sizeError = getBankUploadExcelFileSizeError(file);

      if (isNull(sizeError)) {
        setParseErrors([]);

        return true;
      }

      setParseErrors([sizeError]);

      enqueueSnackbar(formatBankUploadParseError(sizeError, t), {
        variant: 'error',
      });

      return false;
    },
    [enqueueSnackbar, t],
  );

  const parseFile = useCallback(
    async (
      file: File,
      options: ParseBankUploadWorkbookOptions = {},
    ): Promise<BankUploadParseResult> => {
      setIsParsing(true);

      try {
        const result = await parseBankUploadWorkbook(file, entity, options);

        setParseErrors(result.errors);

        if (result.errors.length > 0) {
          console.error('[bank-upload-excel] parse errors', {
            entity,
            fileName: file.name,
            errors: result.errors,
            validCount: result.validCount,
            invalidCount: result.invalidCount,
          });
        } else {
          console.info('[bank-upload-excel] parse ok', {
            entity,
            fileName: file.name,
            validCount: result.validCount,
            locale: result.locale,
          });
        }

        if (!result.ok) {
          const limitError = result.errors.find(error =>
            LIMIT_ERROR_CODES.has(error.code),
          );

          if (isDefined(limitError)) {
            enqueueSnackbar(formatBankUploadParseError(limitError, t), {
              variant: 'error',
            });

            return result;
          }

          const templateError = result.errors.find(error =>
            TEMPLATE_ERROR_CODES.has(error.code),
          );

          if (isDefined(templateError)) {
            enqueueSnackbar(
              t('aggregator.create-bank.excel.import.invalid-template', {
                ns: 'aggregator',
              }),
              { variant: 'error' },
            );

            return result;
          }

          if (result.errors.some(error => error.code === 'empty_data')) {
            enqueueSnackbar(
              t('aggregator.create-bank.excel.import.empty', {
                ns: 'aggregator',
              }),
              { variant: 'warning' },
            );

            return result;
          }

          enqueueSnackbar(
            t('aggregator.create-bank.excel.import.error-with-count', {
              ns: 'aggregator',
              errorCount: result.errors.length,
            }),
            { variant: 'error' },
          );

          return result;
        }

        enqueueSnackbar(
          t('aggregator.create-bank.excel.import.success', {
            ns: 'aggregator',
            count: result.validCount,
          }),
          { variant: 'success' },
        );

        return result;
      } finally {
        setIsParsing(false);
      }
    },
    [entity, enqueueSnackbar, t],
  );

  return {
    isParsing,
    parseErrors,
    parseFile,
    clearParseErrors,
    applyFileSizeLimit,
  };
};
